/**
 * @file PermissionsManager.tsx
 * @brief RBAC Management UI for super_admin to configure role permissions
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import { ChevronDown, Save, RefreshCw, Shield, Lock } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionService, type PagePermission, type FeaturePermission, type PermissionStructure } from '@/services/api/permissionService';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { countEnabledPagesForRole } from '@/shared/utils/pageAccess';

const ROLES = ['super_admin', 'admin', 'manager', 'engineer'];
const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  engineer: 'Engineer',
};

const ROLE_COLORS: Record<string, string> = {
  super_admin: '#9c27b0',
  admin: '#4F46E5',
  manager: '#ff9800',
  engineer: '#4caf50',
};

interface PermissionsManagerProps {
  /** When true, omits outer padding and page title (used inside UsersPage tabs). */
  embedded?: boolean;
}

const PermissionsManager: React.FC<PermissionsManagerProps> = ({ embedded = false }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbar();
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [pageChanges, setPageChanges] = useState<Map<string, Map<string, boolean>>>(new Map());
  const [featureChanges, setFeatureChanges] = useState<Map<string, Map<string, boolean>>>(new Map());

  // Fetch permission structure
  const { data: structure, isLoading: structureLoading } = useQuery({
    queryKey: ['permissions', 'structure'],
    queryFn: () => permissionService.getStructure(),
  });

  // Fetch all page permissions
  const { data: pagePermissions, isLoading: pageLoading } = useQuery({
    queryKey: ['permissions', 'pages'],
    queryFn: () => permissionService.getAllPagePermissions(),
  });

  // Fetch feature permissions for selected page
  const { data: featurePermissions, isLoading: featureLoading } = useQuery({
    queryKey: ['permissions', 'features', selectedPage],
    queryFn: () => permissionService.getFeaturePermissionsByPage(selectedPage!),
    enabled: !!selectedPage,
  });

  // Create permission maps for quick lookup
  const pageAccessMap = useMemo(() => {
    if (!pagePermissions) return new Map<string, Map<string, boolean>>();
    
    const map = new Map<string, Map<string, boolean>>();
    pagePermissions.forEach((perm: PagePermission) => {
      if (!map.has(perm.PAGE_KEY)) {
        map.set(perm.PAGE_KEY, new Map());
      }
      map.get(perm.PAGE_KEY)!.set(perm.ROLE, perm.CAN_ACCESS);
    });
    
    // Apply pending changes
    pageChanges.forEach((roleChanges, pageKey) => {
      if (!map.has(pageKey)) {
        map.set(pageKey, new Map());
      }
      roleChanges.forEach((value, role) => {
        map.get(pageKey)!.set(role, value);
      });
    });
    
    return map;
  }, [pagePermissions, pageChanges]);

  const featureAccessMap = useMemo(() => {
    if (!featurePermissions) return new Map<string, Map<string, boolean>>();
    
    const map = new Map<string, Map<string, boolean>>();
    featurePermissions.forEach((perm: FeaturePermission) => {
      const key = `${perm.FEATURE_KEY}:${perm.ACTION}`;
      if (!map.has(key)) {
        map.set(key, new Map());
      }
      map.get(key)!.set(perm.ROLE, perm.GRANTED);
    });
    
    // Apply pending changes
    featureChanges.forEach((roleChanges, key) => {
      if (!map.has(key)) {
        map.set(key, new Map());
      }
      roleChanges.forEach((value, role) => {
        map.get(key)!.set(role, value);
      });
    });
    
    return map;
  }, [featurePermissions, featureChanges]);

  // Mutations
  const updatePageMutation = useMutation({
    mutationFn: ({ role, updates }: { role: string; updates: { pageKey: string; canAccess: boolean }[] }) =>
      permissionService.updatePageAccess(role, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions', 'pages'] });
      showSuccess('Page permissions updated successfully');
    },
    onError: () => {
      showError('Failed to update page permissions');
    },
  });

  const updateFeatureMutation = useMutation({
    mutationFn: ({ role, pageKey, updates }: { role: string; pageKey: string; updates: { featureKey: string; action: string; granted: boolean }[] }) =>
      permissionService.updateFeaturePermissions(role, pageKey, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions', 'features'] });
      showSuccess('Feature permissions updated successfully');
    },
    onError: () => {
      showError('Failed to update feature permissions');
    },
  });

  // Handle page access toggle
  const handlePageAccessChange = (pageKey: string, role: string, value: boolean) => {
    // Prevent modifying super_admin permissions
    if (role === 'super_admin') {
      showError('Cannot modify Super Admin permissions');
      return;
    }

    const newChanges = new Map(pageChanges);
    if (!newChanges.has(pageKey)) {
      newChanges.set(pageKey, new Map());
    }
    newChanges.get(pageKey)!.set(role, value);
    setPageChanges(newChanges);
  };

  // Handle feature permission toggle
  const handleFeatureChange = (featureKey: string, action: string, role: string, value: boolean) => {
    // Prevent modifying super_admin permissions
    if (role === 'super_admin') {
      showError('Cannot modify Super Admin permissions');
      return;
    }

    const key = `${featureKey}:${action}`;
    const newChanges = new Map(featureChanges);
    if (!newChanges.has(key)) {
      newChanges.set(key, new Map());
    }
    newChanges.get(key)!.set(role, value);
    setFeatureChanges(newChanges);
  };

  // Save page permission changes
  const handleSavePageChanges = async () => {
    if (pageChanges.size === 0) {
      showError('No changes to save');
      return;
    }

    // Engineer must have at least one page enabled before any page-permission save
    const engineerPageCount = countEnabledPagesForRole(pageAccessMap, 'engineer');
    if (engineerPageCount === 0) {
      showError(
        'Select at least one page for Engineer before saving. Engineers cannot use the app with zero page access.'
      );
      return;
    }

    try {
      // Group changes by role
      const changesByRole = new Map<string, { pageKey: string; canAccess: boolean }[]>();
      
      pageChanges.forEach((roleChanges, pageKey) => {
        roleChanges.forEach((canAccess, role) => {
          if (!changesByRole.has(role)) {
            changesByRole.set(role, []);
          }
          changesByRole.get(role)!.push({ pageKey, canAccess });
        });
      });

      // Save each role's changes
      for (const [role, updates] of changesByRole) {
        await updatePageMutation.mutateAsync({ role, updates });
      }

      setPageChanges(new Map());
    } catch (error) {
      // Error handling done in mutation
    }
  };

  // Save feature permission changes
  const handleSaveFeatureChanges = async () => {
    if (featureChanges.size === 0 || !selectedPage) {
      showError('No changes to save');
      return;
    }

    try {
      // Group changes by role
      const changesByRole = new Map<string, { featureKey: string; action: string; granted: boolean }[]>();
      
      featureChanges.forEach((roleChanges, key) => {
        const [featureKey, action] = key.split(':');
        roleChanges.forEach((granted, role) => {
          if (!changesByRole.has(role)) {
            changesByRole.set(role, []);
          }
          changesByRole.get(role)!.push({ featureKey, action, granted });
        });
      });

      // Save each role's changes
      for (const [role, updates] of changesByRole) {
        await updateFeatureMutation.mutateAsync({ role, pageKey: selectedPage, updates });
      }

      setFeatureChanges(new Map());
    } catch (error) {
      // Error handling done in mutation
    }
  };

  // Discard changes
  const handleDiscardChanges = () => {
    setPageChanges(new Map());
    setFeatureChanges(new Map());
  };

  if (structureLoading || pageLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const hasPendingChanges = pageChanges.size > 0 || featureChanges.size > 0;

  return (
    <Box sx={embedded ? undefined : { p: 3 }}>
      <Stack spacing={3}>
        {!embedded && (
          <Box>
            <Typography variant="h5" gutterBottom display="flex" alignItems="center" gap={1}>
              <Shield size={24} />
              Roles & Permissions Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure which roles can access pages and features. Changes are saved per role.
            </Typography>
          </Box>
        )}

        {/* Warning */}
        <Alert severity="warning" icon={<Lock size={18} />}>
          Super Admin permissions cannot be modified to prevent lockout.
        </Alert>

        {countEnabledPagesForRole(pageAccessMap, 'engineer') === 0 && (
          <Alert severity="error">
            Engineer has no pages selected. Select at least one page for Engineer and save before engineers can use the app.
          </Alert>
        )}

        {/* Page Access Matrix */}
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Page Access Permissions</Typography>
                {hasPendingChanges && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<RefreshCw size={16} />}
                      onClick={handleDiscardChanges}
                    >
                      Discard Changes
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<Save size={16} />}
                      onClick={handleSavePageChanges}
                      disabled={updatePageMutation.isPending}
                    >
                      Save Changes
                    </Button>
                  </Stack>
                )}
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Page</TableCell>
                      {ROLES.map((role) => (
                        <TableCell key={role} align="center" sx={{ fontWeight: 'bold' }}>
                          <Chip
                            label={ROLE_LABELS[role]}
                            size="small"
                            sx={{
                              backgroundColor: ROLE_COLORS[role],
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {structure?.map((page: PermissionStructure) => (
                      <TableRow key={page.pageKey} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {page.pageName}
                          </Typography>
                        </TableCell>
                        {ROLES.map((role) => {
                          const hasAccess = pageAccessMap.get(page.pageKey)?.get(role) ?? false;
                          const isLocked = role === 'super_admin';
                          
                          return (
                            <TableCell key={role} align="center">
                              <Checkbox
                                checked={hasAccess}
                                disabled={isLocked}
                                onChange={(e) => handlePageAccessChange(page.pageKey, role, e.target.checked)}
                                size="small"
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </CardContent>
        </Card>

        {/* Feature Permissions */}
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Feature-Level Permissions</Typography>
              <Typography variant="body2" color="text.secondary">
                Select a page to configure granular feature permissions (view, create, edit, delete actions).
              </Typography>

              {structure?.filter((page: PermissionStructure) => page.features.length > 0).map((page: PermissionStructure) => (
                <Accordion
                  key={page.pageKey}
                  expanded={selectedPage === page.pageKey}
                  onChange={() => setSelectedPage(selectedPage === page.pageKey ? null : page.pageKey)}
                >
                  <AccordionSummary expandIcon={<ChevronDown size={20} />}>
                    <Typography fontWeight="medium">{page.pageName}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {featureLoading ? (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {page.features.map((feature) => (
                          <Box key={feature.featureKey}>
                            <Typography variant="subtitle2" gutterBottom>
                              {feature.featureName}
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>Action</TableCell>
                                    {ROLES.map((role) => (
                                      <TableCell key={role} align="center">
                                        <Chip
                                          label={ROLE_LABELS[role]}
                                          size="small"
                                          sx={{
                                            backgroundColor: ROLE_COLORS[role],
                                            color: 'white',
                                            fontSize: '0.7rem',
                                          }}
                                        />
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {feature.actions.map((action) => (
                                    <TableRow key={action} hover>
                                      <TableCell>
                                        <Chip label={action.toUpperCase()} size="small" variant="outlined" />
                                      </TableCell>
                                      {ROLES.map((role) => {
                                        const key = `${feature.featureKey}:${action}`;
                                        const granted = featureAccessMap.get(key)?.get(role) ?? false;
                                        const isLocked = role === 'super_admin';
                                        
                                        return (
                                          <TableCell key={role} align="center">
                                            <Checkbox
                                              checked={granted}
                                              disabled={isLocked}
                                              onChange={(e) => handleFeatureChange(feature.featureKey, action, role, e.target.checked)}
                                              size="small"
                                            />
                                          </TableCell>
                                        );
                                      })}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ))}
                        
                        {featureChanges.size > 0 && (
                          <Box display="flex" justifyContent="flex-end" gap={1} pt={2}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => setFeatureChanges(new Map())}
                            >
                              Discard
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<Save size={16} />}
                              onClick={handleSaveFeatureChanges}
                              disabled={updateFeatureMutation.isPending}
                            >
                              Save Feature Changes
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default PermissionsManager;
